import { parseSubjectResourceType } from "@/utils/subject-resource-type";

const RESOURCE_BLOCK_PATTERN =
  /<resource\b[^>]*>\s*<name>([\s\S]*?)<\/name>\s*<resourceId>([\s\S]*?)<\/resourceId>\s*(?:<mediaId>([\s\S]*?)<\/mediaId>\s*)?<resourceType>\s*([^<]+?)\s*<\/resourceType>\s*<\/resource>/i;

const RESOURCE_CLOSE_TAG = "</resource>";

const VIDEO_URL_RE = /\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i;
const AUDIO_URL_RE = /\.(mp3|wav|m4a|aac|flac|ogg)(\?|#|$)/i;

function isVideoUrl(href) {
  const u = (href || "").trim();
  return Boolean(u && VIDEO_URL_RE.test(u));
}

function isAudioUrl(href) {
  const u = (href || "").trim();
  return Boolean(u && AUDIO_URL_RE.test(u) && !isVideoUrl(u));
}

/**
 * CommonMark 侧翼规则：强调符紧贴 Unicode 标点时不算 left/right-flanking，
 * 例如 **“半殖民地半封建社会”** 会被原样留下。把成对引号/括号移到 ** 外侧即可解析。
 */
function relocateEmphasisAroundWrappingPunct(source) {
  const pairMap = {
    "“": "”",
    "‘": "’",
    '"': '"',
    "'": "'",
    "«": "»",
    "「": "」",
    "『": "』",
    "《": "》",
    "（": "）",
    "(": ")",
    "【": "】",
    "〔": "〕",
    "〈": "〉",
  };
  const openClass = Object.keys(pairMap)
    .map((ch) => ch.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"))
    .join("");
  const closeClass = Object.values(pairMap)
    .map((ch) => ch.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&"))
    .join("");
  const strongRe = new RegExp(`\\*\\*([${openClass}])([\\s\\S]*?)([${closeClass}])\\*\\*`, "g");

  return String(source).replace(strongRe, (match, open, inner, close) => {
    if (pairMap[open] !== close) return match;
    return `${open}**${inner}**${close}`;
  });
}

/** 对齐 RichTextContent.normalizeBookMarkdown */
export function normalizeBookMarkdown(source) {
  if (!source) return "";
  return relocateEmphasisAroundWrappingPunct(
    String(source)
      .replace(/\r\n/g, "\n")
      .replace(/^\*\s+\[X\]\s/gim, "- [x] ")
      .replace(/^\*\s+\[x\]\s/gim, "- [x] ")
      .replace(/^\*\s+\[\s\]\s/gim, "- [ ] ")
      .replace(/^[ \t]+(\|.*\|)\s*$/gm, "$1")
      .replace(/([^\n|])\n(\|[^\n]+\|)/g, "$1\n\n$2")
  );
}

function enhanceMarkdownMedia(html) {
  if (!html) return "";
  return html.replace(/<a\s+([^>]*?)href="([^"]+)"([^>]*)>([\s\S]*?)<\/a>/gi, (match, before, href, after, children) => {
    const url = (href || "").trim();
    if (isVideoUrl(url)) {
      return `<video src="${url}" controls playsinline preload="metadata">${children}</video>`;
    }
    if (isAudioUrl(url)) {
      return `<audio src="${url}" controls preload="metadata">${children}</audio>`;
    }
    const attrs = `${before}href="${href}"${after}`;
    if (/target\s*=/i.test(attrs)) {
      return `<a ${attrs}>${children}</a>`;
    }
    return `<a ${attrs} target="_blank" rel="noopener noreferrer">${children}</a>`;
  });
}

export function hasCompleteResourceMarkup(text) {
  const lower = text.toLowerCase();
  if (!lower.includes("<resource")) return false;
  const closeIdx = lower.indexOf(RESOURCE_CLOSE_TAG);
  if (closeIdx === -1) return false;
  const block = text.slice(0, closeIdx + RESOURCE_CLOSE_TAG.length);
  return RESOURCE_BLOCK_PATTERN.test(block);
}

export function stripBrokenResourceMarkup(text) {
  let next = text.replace(new RegExp(RESOURCE_BLOCK_PATTERN.source, "gi"), "");
  next = next.replace(/<resource\b[^>]*>[\s\S]*$/gi, "");
  next = next.replace(/<\/?(?:resource|name|resourceId|mediaId|resourceType)\b[^>]*>/gi, "");
  return next.trim();
}

export function parseAiAskResourceSegments(text) {
  if (!text) return [];
  const segments = [];
  const reg = new RegExp(RESOURCE_BLOCK_PATTERN.source, "gi");
  let lastIndex = 0;
  let match;
  while ((match = reg.exec(text)) !== null) {
    const start = match.index;
    if (start > lastIndex) {
      const rawText = stripBrokenResourceMarkup(text.slice(lastIndex, start));
      if (rawText) segments.push({ type: "text", value: rawText });
    }
    segments.push({
      type: "resource",
      value: {
        name: match[1].trim(),
        resourceId: match[2].trim(),
        mediaId: (match[3] ?? "").trim(),
        resourceType: parseSubjectResourceType(match[4]),
      },
    });
    lastIndex = reg.lastIndex;
  }
  if (lastIndex < text.length) {
    const rawText = stripBrokenResourceMarkup(text.slice(lastIndex));
    if (rawText) segments.push({ type: "text", value: rawText });
  }
  return segments;
}

export function takeTypingQueueSlice(queue) {
  if (!queue) return null;
  const lower = queue.toLowerCase();
  const resourceStart = lower.indexOf("<resource");
  if (resourceStart === -1) {
    return { slice: queue[0], rest: queue.slice(1) };
  }
  if (resourceStart > 0) {
    return { slice: queue[0], rest: queue.slice(1) };
  }
  const closeIdx = lower.indexOf(RESOURCE_CLOSE_TAG);
  if (closeIdx === -1) return null;
  const end = closeIdx + RESOURCE_CLOSE_TAG.length;
  const block = queue.slice(0, end);
  if (!hasCompleteResourceMarkup(block)) return null;
  return { slice: block, rest: queue.slice(end) };
}

export function renderAssistantMarkdown(text) {
  if (!text) return "";
  const normalized = normalizeBookMarkdown(String(text).replace(/\\n/g, "\n"));
  if (typeof marked !== "undefined" && marked.parse) {
    return enhanceMarkdownMedia(marked.parse(normalized));
  }
  return normalized
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br/>");
}
